import { LitElement, css, html, PropertyValues, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@banno/jha-wc/src/jha-dialog/jha-dialog";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/forms/jha-form-input-base/jha-form-input";
import "@banno/jha-wc/src/forms/jha-form-select/jha-form-select";
import "@banno/jha-wc/src/icons/jha-icon-document";
import "@banno/jha-wc/src/icons/jha-icon-information";
import "@banno/jha-wc/src/icons/jha-icon-code";
import "@banno/jha-wc/src/icons/jha-icon-wand-magic-sparkles";  // 🧪 DEV ONLY: For auto-fill button (only visible in local development)
import "@banno/jha-wc/src/icons/jha-icon-checkmark";
import "@banno/jha-wc/src/icons/jha-icon-warning-filled";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import { WorkflowTemplateVariable } from "../../types/WorkflowTemplates/WorkflowTemplateVariable";
import Cookies from "js-cookie";
import {
  decryptValue,
  getEnvApiUrl,
  isBusinessUnitAdminUserType,
  showToastMessage,
} from "../../utils/app-utils";
import { openJhftDialog } from "../../Components/JhftJhaDialog";
import { Apis } from "../../utils/api-constants";
import { getRoleData } from "../../utils/getRoleData";
import { postRequest, getRequest, fixedEncodeURIComponent } from "../../utils/jha-fetch";
import {
  isBuiltinBusUnitVariableName,
  isBuiltinRegionVariableName,
  isGlobalTemplateCategory,
} from "../../utils/workflowTemplatePlaceholderSyntax";
import {
  AZURE_CONNECTION_STRING_FORMAT_WARNING,
  isValidAzureStorageConnectionString,
} from "../../utils/azure-storage-connection-string";
import {
  AWS_IAM_ROLE_ARN_FORMAT_WARNING,
  isValidAwsIamRoleArn,
} from "../../utils/aws-iam-role-arn";
import { getErrorMessage } from "../../utils/getErrorMessage";
import {
  INVALID_TEMPLATE_REGEX_SYNTAX_MESSAGE,
  REGEX_RULE_MAX_TEST_VALUE_LENGTH,
} from "../../utils/regex-rule-safety";

/**
 * WorkflowTemplateGenerateDialog
 * 
 * A custom form element that collects variable values for generating a workflow from a template.
 * This component is designed to be used with DialogUtil.awaitDialog for modal display.
 * 
 * Features:
 * - Two-column grid layout for variable inputs
 * - Real-time regex validation
 * - Tooltips for variable descriptions and regex rules
 * - Vertically scrollable content area
 * - Variables displayed in array order (order preserved from DynamoDB)
 * 
 * Usage:
 * ```typescript
 * const formElement = document.createElement('workflow-template-generate-dialog');
 * formElement.variables = variablesArray;
 * formElement.workflowTemplateId = templateId;
 * formElement.templateName = templateName;
 * 
 * await DialogUtil.awaitDialog({
 *   el: formElement,
 *   confirmCallback: async () => {
 *     if (!formElement.isValid()) {
 *       throw new Error("Validation failed");
 *     }
 *   }
 * });
 * 
 * const values = formElement.getValues();
 * ```
 */
@customElement("workflow-template-generate-dialog")
export class WorkflowTemplateGenerateDialog extends (formElementMixin(LitElement) as unknown as typeof LitElement) {
  static get is() {
    return "workflow-template-generate-dialog";
  }

  /**
   * BU-scoped template rows must carry `businessUnitId`. If missing, generation could target the
   * wrong institution — block the UI and direct users to support.
   */
  private static readonly BU_SCOPED_MISSING_PINNED_MESSAGE =
    "This template is misconfigured: it is scoped to a business unit but no business unit is set. " +
    "You cannot generate a workflow from this template. Please contact support so the template can be repaired.";

  private static readonly BU_FIELD_LOCKED_TO_TEMPLATE_TOOLTIP =
    "This template is tied to this business unit. The selection cannot be changed when generating a workflow.";

  // Properties
  @property({ type: String }) workflowTemplateId: string = "";
  @property({ type: String }) templateName: string = "";
  @property({ type: Array }) variables: WorkflowTemplateVariable[] = [];
  @property({ type: String }) transferType: string = ""; // "pull" or "push"
  /** Raw template `category` (e.g. "global", "businessunit", or legacy "ALL"/"DEFAULT"). Drives BU dropdown branching. */
  @property({ type: String }) templateCategory: string = "";
  /** Pinned BU id for non-Global templates; null for Global templates. */
  @property({ type: String }) templateBusinessUnitId: string | null = null;
  /** Display name for the pinned BU on non-Global templates (looked up from businessUnitsMap by parent). */
  @property({ type: String }) templateBusinessUnitName: string = "";

  // State
  @state() private variableValues: Record<string, string> = {};
  @state() private validationErrors: Record<string, string> = {};
  @state() private isGenerating = false;
  @state() private apiResult: { success: boolean; message: string; workflowName?: string } | null = null;

  /** Prevents re-entrancy while openJhftDialog is stacked over this generate dialog. */
  private _closePromptOpen = false;
  /** Allow jha-dialog cancel to reach DialogUtil after Leave / force close. */
  private _forceClose = false;
  /** Set when the user leaves mid-generate, so the late response stays silent. */
  private _generateAbandoned = false;
  /** Bumped on each generate start and on abandon so a late request cannot write. */
  private _generateSeq = 0;

  /** AWS regions for built-in `BUILTIN_REGION_VARIABLE` (same API + fallback as workflow step editor). */
  @state() private awsRegionOptions: { value: string; name: string }[] = [];
  @state() private awsRegionsLoading = false;

  private awsRegionsFetchPromise: Promise<void> | null = null;

  /** Active Business Units returned by the application's BU values API (used as the master list for `BUILTIN_BUSUNIT_VARIABLE`). */
  @state() private activeBusinessUnitOptions: { value: string; name: string }[] = [];
  @state() private businessUnitsLoading = false;

  private businessUnitsFetchPromise: Promise<void> | null = null;
  /** Ensures the auto-selected BU value is applied exactly once after the BU list and template inputs settle. */
  private busUnitDefaultApplied = false;

  /** Matches `.variables-grid` single-column breakpoint — tooltip side alternates in 2-col but should always open right in 1-col. */
  @state() private narrowVariableGrid = false;
  private _variableGridMq?: MediaQueryList;
  private _onVariableGridMqChange?: () => void;

  /**
   * Portal-based tooltip state. Rendered at the dialog root with `position: fixed` so it
   * escapes any ancestor `overflow: hidden|auto` (the variables scroll container clips
   * pseudo-element-based tooltips like `jha-tooltip`).
   */
  @state() private customTooltip: {
    text: string;
    x: number;
    y: number;
    side: "top" | "bottom" | "left" | "right";
  } | null = null;

  private _tooltipHideTimer: ReturnType<typeof setTimeout> | null = null;
  private _onScrollOrResize = () => this.hideCustomTooltip();

  // Track if form is dirty
  private initialValues: Record<string, string> = {};

  // Track whether the cancel is from a backdrop click
  private isBackdropClick = false;

  // ========================================
  // 🧪 DEVELOPMENT HELPER CONFIGURATION
  // ========================================
  /**
   * Determines if development helper features (like auto-fill) should be shown.
   * 
   * These helpers are ONLY visible when:
   * 1. Running in Vite's development mode (npm run dev)
   * 2. AND accessing via localhost or 127.0.0.1
   * 
   * In production builds:
   * - The button is hidden via this runtime check
   * - The helper methods remain in the bundle but are never executed
   * - This approach was chosen over build-time stripping for simplicity
   * 
   * Why keep the code?
   * - Useful for developers testing the Generate Workflow dialog
   * - Auto-fills all variable inputs with valid test data matching regex patterns
   * - Saves significant time when testing templates with many variables
   * 
   * Bundle impact: ~300 lines / ~10-15KB (acceptable trade-off for dev convenience)
   */
  private get showDevHelpers(): boolean {
    return import.meta.env.DEV &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1');
  }

  /** Read-only users cannot submit; skip "unsaved changes" when closing the dialog. */
  private get isReadOnlyUser(): boolean {
    return /readonly/i.test(decryptValue(Cookies.get("userRole") || ""));
  }

  // Modal behavior properties
  // Prevents backdrop clicks from closing the dialog (checked by DialogUtil)
  // CRITICAL: reflect: true ensures DialogUtil can read this property
  @property({ type: Boolean, reflect: true })
  hideCancel = true;

  connectedCallback() {
    super.connectedCallback();
    this._forceClose = false;
    this._closePromptOpen = false;
    this.attachVariableGridMediaQuery();
    // Store initial values for dirty checking
    this.initialValues = { ...this.variableValues };
    // Bind event handlers
    this.handleDialogCancel = this.handleDialogCancel.bind(this);
    this.handleDialogClick = this.handleDialogClick.bind(this);

    // Listen for clicks to detect backdrop clicks
    document.addEventListener("click", this.handleDialogClick, true);

    // Listen for cancel events using capture phase (true) - this catches ESC and backdrop
    document.addEventListener("cancel", this.handleDialogCancel, true);
    // firstUpdated does not re-run on re-attach; bind here so X still hits the dirty gate.
    this.bindJhaDialogCancel();

    // Hide the portal tooltip if the page scrolls or window resizes — its
    // anchor rect would no longer line up.
    window.addEventListener("scroll", this._onScrollOrResize, true);
    window.addEventListener("resize", this._onScrollOrResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.detachVariableGridMediaQuery();
    // Clean up event listeners
    document.removeEventListener("click", this.handleDialogClick, true);
    document.removeEventListener("cancel", this.handleDialogCancel, true);
    const jhaDialog = this.shadowRoot?.querySelector("jha-dialog");
    jhaDialog?.removeEventListener("cancel", this.handleJhaDialogCancel, true);
    window.removeEventListener("scroll", this._onScrollOrResize, true);
    window.removeEventListener("resize", this._onScrollOrResize);
    if (this._tooltipHideTimer) {
      clearTimeout(this._tooltipHideTimer);
      this._tooltipHideTimer = null;
    }
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated?.(_changedProperties);
    // First paint: jha-dialog is not in the tree during the initial connectedCallback.
    this.bindJhaDialogCancel();
  }

  /**
   * X on inner jha-dialog fires CustomEvent('cancel') → DialogUtil closes immediately.
   * Intercept so X uses the same dirty gate as ESC. Idempotent for reconnect.
   */
  private bindJhaDialogCancel() {
    const jhaDialog = this.shadowRoot?.querySelector("jha-dialog");
    jhaDialog?.removeEventListener("cancel", this.handleJhaDialogCancel, true);
    jhaDialog?.addEventListener("cancel", this.handleJhaDialogCancel, true);
  }

  private attachVariableGridMediaQuery(): void {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }
    const mq = window.matchMedia("(max-width: 768px)");
    this._variableGridMq = mq;
    this._onVariableGridMqChange = () => {
      this.narrowVariableGrid = mq.matches;
    };
    this.narrowVariableGrid = mq.matches;
    mq.addEventListener("change", this._onVariableGridMqChange);
  }

  private detachVariableGridMediaQuery(): void {
    if (this._variableGridMq && this._onVariableGridMqChange) {
      this._variableGridMq.removeEventListener("change", this._onVariableGridMqChange);
    }
    this._variableGridMq = undefined;
    this._onVariableGridMqChange = undefined;
  }

  /** Label icon tooltips: open toward dialog center — right column uses `left`, left column uses `right`; single-column layout uses `right` only. */
  private labelTooltipOpensRight(variableIndex: number): boolean {
    if (this.narrowVariableGrid) {
      return true;
    }
    return variableIndex % 2 === 0;
  }

  // ============================================================================
  // PORTAL TOOLTIP — replaces jha-tooltip inside the scrollable dialog content.
  //
  // jha-tooltip renders via :host::before/::after pseudo-elements positioned
  // absolutely, which means any ancestor `overflow: hidden|auto` (the variables
  // scroll container, the dialog body, the form-floating-group on selects, etc.)
  // clips the tooltip. A `position: fixed` element rendered at the component
  // root escapes ALL overflow contexts (and even shadow boundaries for paint).
  // ============================================================================

  private showCustomTooltip(
    e: Event,
    text: string,
    side: "top" | "bottom" | "left" | "right" = "right"
  ): void {
    if (!text) return;
    if (this._tooltipHideTimer) {
      clearTimeout(this._tooltipHideTimer);
      this._tooltipHideTimer = null;
    }
    const trigger = e.currentTarget as HTMLElement | null;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 8;
    let x = 0;
    let y = 0;
    switch (side) {
      case "right":
        x = rect.right + gap;
        y = rect.top + rect.height / 2;
        break;
      case "left":
        x = rect.left - gap;
        y = rect.top + rect.height / 2;
        break;
      case "bottom":
        x = rect.left + rect.width / 2;
        y = rect.bottom + gap;
        break;
      case "top":
        x = rect.left + rect.width / 2;
        y = rect.top - gap;
        break;
    }
    this.customTooltip = { text, x, y, side };
  }

  private hideCustomTooltip(): void {
    if (this._tooltipHideTimer) {
      clearTimeout(this._tooltipHideTimer);
    }
    // Tiny delay avoids flicker when moving between adjacent triggers.
    this._tooltipHideTimer = setTimeout(() => {
      this.customTooltip = null;
      this._tooltipHideTimer = null;
    }, 50);
  }

  /**
   * Suppress browser-native HTML5 validation tooltips on `<select required>` (e.g.
   * "Please select an item in the list.") inside our jha-form-select elements.
   * We do our own validation and present errors via the field's warning state,
   * so the duplicate native tooltip is just visual clutter on top of our jha-tooltip.
   *
   * Strategy: set `noValidate` on the inner shadow `<form>` and clear/blank any `title`
   * the underlying form library may have set. Safe to run on every update.
   */
  private suppressNativeFormValidationTooltips(): void {
    const root = this.shadowRoot;
    if (!root) return;
    const selects = root.querySelectorAll("jha-form-select");
    selects.forEach((sel) => {
      const sr = (sel as HTMLElement & { shadowRoot?: ShadowRoot | null }).shadowRoot;
      if (!sr) return;
      const innerForm = sr.querySelector("form") as HTMLFormElement | null;
      if (innerForm && !innerForm.noValidate) {
        innerForm.noValidate = true;
      }
      const innerSelect = sr.querySelector("select") as HTMLSelectElement | null;
      if (innerSelect) {
        if (innerSelect.title) {
          innerSelect.title = "";
        }
        // Clearing customValidity ensures no leftover message can be shown.
        try {
          innerSelect.setCustomValidity("");
        } catch {
          /* ignore */
        }
      }
    });
  }

  protected updated(changedProperties: PropertyValues<this>): void {
    super.updated(changedProperties);
    if (changedProperties.has("variables") && this.variablesIncludeBuiltinRegion()) {
      void this.ensureAwsRegionsLoaded();
    }
    if (
      (changedProperties.has("variables") ||
        changedProperties.has("templateCategory") ||
        changedProperties.has("templateBusinessUnitId") ||
        changedProperties.has("templateBusinessUnitName")) &&
      this.variablesIncludeBuiltinBusUnit()
    ) {
      void this.ensureBusinessUnitsLoaded();
    }
    if (this.variablesIncludeBuiltinBusUnit()) {
      this.applyBusUnitAutoSelectIfReady();
    }
    // Strip any browser-native HTML5 validation tooltip from required <select>s
    // so it doesn't duplicate (and visually clash with) our jha-tooltip.
    this.suppressNativeFormValidationTooltips();
  }

  private variablesIncludeBuiltinRegion(): boolean {
    return this.variables.some((v) => isBuiltinRegionVariableName(v.name));
  }

  private variablesIncludeBuiltinBusUnit(): boolean {
    return this.variables.some((v) => isBuiltinBusUnitVariableName(v.name));
  }

  /**
   * Loads `Apis.AWS_REGION_LIST` once when the template includes `BUILTIN_REGION_VARIABLE`.
   * Matches AddWorkflowStepPopup fallback when the API is empty or errors.
   */
  private async ensureAwsRegionsLoaded(): Promise<void> {
    if (!this.variablesIncludeBuiltinRegion()) {
      return;
    }
    if (this.awsRegionOptions.length > 0) {
      return;
    }
    if (!this.awsRegionsFetchPromise) {
      this.awsRegionsFetchPromise = this.loadAwsRegionOptionsFromApi().finally(() => {
        this.awsRegionsFetchPromise = null;
      });
    }
    await this.awsRegionsFetchPromise;
  }

  private async loadAwsRegionOptionsFromApi(): Promise<void> {
    const fallbackRegions = [
      { value: "us-east-1", name: "us-east-1" },
      { value: "us-east-2", name: "us-east-2" },
      { value: "us-west-1", name: "us-west-1" },
      { value: "us-west-2", name: "us-west-2" },
      { value: "eu-west-1", name: "eu-west-1" },
      { value: "eu-central-1", name: "eu-central-1" },
      { value: "ap-southeast-1", name: "ap-southeast-1" },
      { value: "ap-southeast-2", name: "ap-southeast-2" },
      { value: "ap-northeast-1", name: "ap-northeast-1" },
    ];

    this.awsRegionsLoading = true;
    this.requestUpdate();

    try {
      // getEnvApiUrl: localhost-only; .env.local never ships — no effect in prod, test, staging, or CI builds.
      const regionData = await getRequest(getEnvApiUrl(Apis.AWS_REGION_LIST));

      if (regionData?.list && Array.isArray(regionData.list) && regionData.list.length > 0) {
        this.awsRegionOptions = getRoleData(regionData.list).map((region) => {
          region.name = region.value;
          return region;
        });
      } else {
        console.warn(
          "WorkflowTemplateGenerateDialog: Using fallback AWS Region data — API returned empty response"
        );
        this.awsRegionOptions = fallbackRegions;
      }
    } catch (error) {
      console.error(
        "WorkflowTemplateGenerateDialog: Failed to load AWS Regions from API, using fallback data:",
        error
      );
      this.awsRegionOptions = fallbackRegions;
    } finally {
      this.awsRegionsLoading = false;
      this.requestUpdate();
    }
  }

  private isBuiltinRegionValueAllowed(value: string): boolean {
    const t = (value || "").trim();
    if (!t) {
      return false;
    }
    return this.awsRegionOptions.some((o) => o.value === t);
  }

  // ============================================================================
  // BUILTIN_BUSUNIT_VARIABLE — Business Unit dropdown for the Generate dialog
  // ============================================================================

  /** Logged-in user's Business Unit (set at login). Comparisons are lower-cased. */
  private get loggedUserBu(): string {
    return (Cookies.get("businessUnit") || "").trim().toLowerCase();
  }

  /** True when the logged-in user is a Business Unit Admin (not Enterprise Admin). */
  private get isBusinessUnitAdmin(): boolean {
    return isBusinessUnitAdminUserType(decryptValue(Cookies.get("userType") || ""));
  }

  /** True when the current template's category should be treated as Global (incl. legacy ALL/DEFAULT). */
  private get isGlobalTemplate(): boolean {
    return isGlobalTemplateCategory(this.templateCategory);
  }

  /**
   * Loads `Apis.GET_ALL_BUSINESSUNITVALUES` once when the variables list includes the BUSUNIT
   * built-in. Filters to active BUs and sorts by name (matches WorkflowTemplateSettingsTab).
   */
  private async ensureBusinessUnitsLoaded(): Promise<void> {
    if (!this.variablesIncludeBuiltinBusUnit()) {
      return;
    }
    if (this.activeBusinessUnitOptions.length > 0) {
      return;
    }
    if (!this.businessUnitsFetchPromise) {
      this.businessUnitsFetchPromise = this.loadBusinessUnitOptionsFromApi().finally(() => {
        this.businessUnitsFetchPromise = null;
      });
    }
    await this.businessUnitsFetchPromise;
  }

  private async loadBusinessUnitOptionsFromApi(): Promise<void> {
    this.businessUnitsLoading = true;
    this.requestUpdate();
    try {
      // getEnvApiUrl: localhost-only; .env.local never ships — no effect in prod, test, staging, or CI builds.
      const response = await getRequest(getEnvApiUrl(Apis.GET_ALL_BUSINESSUNITVALUES));
      const list = (response?.businessUnitValues ?? []) as Array<{
        value?: string;
        name?: string;
        status?: string;
      }>;
      this.activeBusinessUnitOptions = list
        .filter((bu) => (bu?.status ?? "").toLowerCase() === "active")
        .map((bu) => ({ value: String(bu.value ?? ""), name: String(bu.name ?? "") }))
        .filter((bu) => bu.value && bu.name)
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error(
        "WorkflowTemplateGenerateDialog: Failed to load Business Units; required-field validation will block submit.",
        error
      );
      this.activeBusinessUnitOptions = [];
    } finally {
      this.businessUnitsLoading = false;
      this.requestUpdate();
    }
  }

  /** Lookup helper: returns the active-list display name for the given BU id, or `fallback` when not found. */
  private lookupActiveBusinessUnitName(value: string, fallback: string): string {
    const v = (value || "").trim();
    if (!v) return fallback;
    const vLower = v.toLowerCase();
    const match = this.activeBusinessUnitOptions.find((bu) => bu.value.toLowerCase() === vLower);
    return match?.name || fallback;
  }

  /** True when visibility is **Business unit** and the template has a non-empty `businessUnitId`. */
  private get hasPinnedBuOnBuScopedTemplate(): boolean {
    return (
      (this.templateCategory ?? "").trim().toLowerCase() === "businessunit" &&
      !!(this.templateBusinessUnitId ?? "").trim()
    );
  }

  /**
   * **Business unit** visibility requires a pinned id. Missing id is invalid data — block generation.
   */
  private get isBuScopedTemplateMissingPinnedBu(): boolean {
    return (
      (this.templateCategory ?? "").trim().toLowerCase() === "businessunit" &&
      !(this.templateBusinessUnitId ?? "").trim()
    );
  }

  /**
   * Returns the options shown in the BU dropdown for the current role and template category.
   *
   * - BU-scoped template with a pinned BU (`templateBusinessUnitId`): always exactly one option —
   *   that pinned BU — for every user role. The control is locked; the workflow must be generated
   *   for that BU only.
   * - Business Unit Admin + Global template: a single option for the BUA's own BU (from the
   *   `businessUnit` cookie). The dropdown is locked.
   * - Enterprise Admin (or any non-BUA) + Global template: all active Business Units from the API
   *   (`activeBusinessUnitOptions`). Legacy `"ALL"` / `"DEFAULT"` template *category* values are
   *   still treated as Global by `isGlobalTemplateCategory`; those strings are never option values.
   * - BU-scoped template with *no* pinned id (misconfigured): no options; field is disabled with
   *   support messaging — never the full active BU list.
   */
  public buildBusinessUnitOptionsForUi(): { value: string; name: string }[] {
    if (this.isBuScopedTemplateMissingPinnedBu) {
      return [];
    }

    if (this.hasPinnedBuOnBuScopedTemplate) {
      const pinnedId = (this.templateBusinessUnitId ?? "").trim();
      const pinnedLower = pinnedId.toLowerCase();
      const activeMatch = this.activeBusinessUnitOptions.find(
        (o) => o.value.toLowerCase() === pinnedLower
      );
      const value = activeMatch ? activeMatch.value : pinnedId;
      const name =
        (this.templateBusinessUnitName || "").trim() ||
        activeMatch?.name ||
        this.lookupActiveBusinessUnitName(pinnedId, pinnedId);
      return [{ value, name }];
    }

    if (this.isBusinessUnitAdmin) {
      const id = this.loggedUserBu;
      if (!id) {
        return [];
      }
      const activeMatch = this.activeBusinessUnitOptions.find((o) => o.value.toLowerCase() === id);
      const value = activeMatch ? activeMatch.value : id;
      const name = activeMatch?.name ?? this.lookupActiveBusinessUnitName(id, id);
      return [{ value, name }];
    }

    return [...this.activeBusinessUnitOptions];
  }

  /**
   * Computes the initial Business Unit field value before the user interacts.
   *
   * BU-scoped templates with a pinned id: resolves `templateBusinessUnitId` to the
   * canonically-cased value from `buildBusinessUnitOptionsForUi()` (a single locked option).
   *
   * Global + Business Unit Admin: the BUA's own BU (`businessUnit` cookie); matches the single
   * dropdown option.
   *
   * Global + Enterprise Admin (or any non-BUA): if the `businessUnit` cookie matches an active BU
   * (case-insensitive), that option's value; else if exactly one active BU exists, that BU;
   * else empty string so the placeholder stays until the user chooses.
   */
  public computeAutoSelectedBuValue(): string {
    if (!this.isGlobalTemplate) {
      const pinnedId = (this.templateBusinessUnitId ?? "").trim();
      if (!pinnedId) {
        return "";
      }
      // Bind to the canonically-cased option when `buildBusinessUnitOptionsForUi()` contains a
      // matching value; otherwise use the raw template id (e.g. synthetic option for inactive BUs).
      const options = this.buildBusinessUnitOptionsForUi();
      const match = options.find((o) => o.value.toLowerCase() === pinnedId.toLowerCase());
      return match ? match.value : pinnedId;
    }
    if (this.isBusinessUnitAdmin && this.loggedUserBu) {
      const options = this.buildBusinessUnitOptionsForUi();
      const match = options.find((o) => o.value.toLowerCase() === this.loggedUserBu);
      return match ? match.value : this.loggedUserBu;
    }
    // Global + non-BUA: prefer cookie BU when it matches an active option; else auto-pick when
    // there is exactly one active BU; otherwise leave blank (user must choose).
    const activeOptions = this.activeBusinessUnitOptions;
    const cookieBu = this.loggedUserBu;
    if (cookieBu) {
      const cookieMatch = activeOptions.find((o) => o.value.toLowerCase() === cookieBu);
      if (cookieMatch) return cookieMatch.value;
    }
    if (activeOptions.length === 1) {
      return activeOptions[0].value;
    }
    return "";
  }

  /**
   * True when the BU dropdown is disabled: Business Unit Admins are locked to their own BU on
   * Global templates; any user generating from a BU-scoped template with a pinned BU is locked to
   * that BU only; misconfigured BU-scoped templates without a pinned id are also disabled.
   */
  private get busUnitDropdownIsLocked(): boolean {
    return (
      this.isBusinessUnitAdmin ||
      this.hasPinnedBuOnBuScopedTemplate ||
      this.isBuScopedTemplateMissingPinnedBu
    );
  }

  /**
   * Applies `computeAutoSelectedBuValue()` to `variableValues` once, after the inputs settle,
   * unless the user has already typed/selected something. Idempotent via `busUnitDefaultApplied`.
   */
  private applyBusUnitAutoSelectIfReady(): void {
    if (this.busUnitDefaultApplied) return;
    const variable = this.variables.find((v) => isBuiltinBusUnitVariableName(v.name));
    if (!variable) return;

    // Global + any role: BU options come from the active BU API list (cookie / single-BU heuristics /
    // BUA locked option). Wait until loading completes so option labels and canonical ids match the API.
    if (this.isGlobalTemplate && this.businessUnitsLoading) {
      return;
    }

    const key = this.getVariableKey(variable);
    if ((this.variableValues[key] || "").trim() !== "") {
      this.busUnitDefaultApplied = true;
      return;
    }
    const auto = this.computeAutoSelectedBuValue();
    if (!auto) {
      // No default yet (e.g. EA + Global with multiple active BUs and no cookie match). Leave
      // placeholder; `computeAutoSelectedBuValue` will run again on the next update if inputs change.
      return;
    }
    this.variableValues = { ...this.variableValues, [key]: auto };
    this.busUnitDefaultApplied = true;
    // Keep dirty baseline in sync — auto-select is not a user edit (ACME-1050).
    this.initialValues = { ...this.initialValues, [key]: auto };
  }

  private isBuiltinBusUnitValueAllowed(value: string): boolean {
    const t = (value || "").trim();
    if (!t) return false;
    // Case-insensitive whitelist check: option `value` is the lowercase BU slug, but the
    // selected value may have been pre-populated from the template's pinned BU id which
    // historically may have been stored with mixed casing.
    const tLower = t.toLowerCase();
    return this.buildBusinessUnitOptionsForUi().some((o) => o.value.toLowerCase() === tLower);
  }

  /**
   * Detect backdrop clicks and PREVENT them (Windows-like modal).
   */
  private handleDialogClick(e: Event) {
    const hostDialog = this.closest("dialog");
    if (!hostDialog || !hostDialog.contains(this)) {
      return;
    }

    // Click directly on dialog element (outside panel) = backdrop
    if (e.target === hostDialog) {
      this.isBackdropClick = true;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      // If DialogUtil does not follow with cancel, do not leave the flag latched
      // (that would swallow the next real ESC).
      queueMicrotask(() => {
        this.isBackdropClick = false;
      });
    }
  }

  /**
   * Check if form has unsaved changes.
   * Public so DialogUtil can call it from cancelCallback.
   */
  public isFormDirty(): boolean {
    const keys = new Set([
      ...Object.keys(this.variableValues),
      ...Object.keys(this.initialValues),
    ]);
    for (const key of keys) {
      if ((this.variableValues[key] || "") !== (this.initialValues[key] || "")) {
        return true;
      }
    }
    return false;
  }

  /**
   * Show confirmation before closing with unsaved changes.
   */
  async showUnsavedConfirmation(): Promise<boolean> {
    this._closePromptOpen = true;
    try {
      const result = await openJhftDialog({
        variant: "warning",
        title: "Unsaved Changes",
        message: "You have unsaved changes. Are you sure you want to leave without saving?",
        cancelLabel: "Stay",
        confirmLabel: "Leave Without Saving",
      });
      return result === "confirm";
    } finally {
      this._closePromptOpen = false;
    }
  }

  /**
   * Confirm abandoning the dialog while generate request is in flight.
   */
  private async confirmAbandonDuringGenerate(): Promise<boolean> {
    this._closePromptOpen = true;
    try {
      const result = await openJhftDialog({
        variant: "warning",
        title: "Generation In Progress",
        message:
          "Workflow generation is in progress. Closing now will not cancel the server request — the workflow may still be created. If created, delete it from the Workflows page. Leave anyway?",
        cancelLabel: "Stay",
        confirmLabel: "Leave Anyway",
      });
      return result === "confirm";
    } finally {
      this._closePromptOpen = false;
    }
  }

  /**
   * jha-dialog header X. DialogUtil listens for bubbled `cancel` and closes without dirty checks.
   */
  private handleJhaDialogCancel = async (e: Event) => {
    if (this._forceClose) {
      return;
    }
    if (this._closePromptOpen) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (this.isGenerating) {
      e.stopImmediatePropagation();
      e.preventDefault();
      if (await this.confirmAbandonDuringGenerate()) {
        this.abandonGenerate();
      }
      return;
    }
    if (this.isReadOnlyUser || !this.isFormDirty()) {
      return;
    }
    e.stopImmediatePropagation();
    e.preventDefault();
    if (await this.showUnsavedConfirmation()) {
      this.dispatchForceClose();
    }
  };

  /**
   * Native &lt;dialog&gt; cancel (ESC). Same dirty gate as the X button.
   */
  async handleDialogCancel(e: Event) {
    if (this._forceClose) {
      return;
    }
    const target = e.target as Node | null;
    const hostDialog = this.closest("dialog");
    if (!hostDialog || target !== hostDialog) {
      return;
    }

    // Backdrop-triggered cancel — never close. Consume the flag here (not a timer).
    if (this.isBackdropClick) {
      e.preventDefault();
      e.stopPropagation();
      this.isBackdropClick = false;
      return;
    }

    if (this._closePromptOpen) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (this.isGenerating) {
      e.preventDefault();
      e.stopPropagation();
      if (await this.confirmAbandonDuringGenerate()) {
        this.abandonGenerate();
      }
      return;
    }

    if (this.isReadOnlyUser || !this.isFormDirty()) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (await this.showUnsavedConfirmation()) {
      this.dispatchForceClose();
    }
  }

  /**
   * Leave while the generate request is still in flight.
   *
   * The request keeps running server-side, and its response still resolves in
   * `onGenerateClick`. Mark it abandoned so that late result cannot open a success or error
   * dialog over whatever page the user moved on to after this one closed.
   */
  private abandonGenerate() {
    showToastMessage(
      "Dialog closed. Check Workflows page - if created, delete it there.",
      "danger",
    );
    this._generateSeq += 1;
    this._generateAbandoned = true;
    this.isGenerating = false;
    this.apiResult = null;
    this.dispatchForceClose();
  }

  /**
   * Re-dispatch cancel so DialogUtil closes the shell after Leave.
   * Leave `_forceClose` true until disconnect so a late/bubbled cancel cannot
   * re-open the prompt. Do not re-attach document listeners after teardown.
   */
  private dispatchForceClose() {
    this._forceClose = true;
    const dialog = this.shadowRoot?.querySelector("jha-dialog");
    (dialog as any)?.dispatchDialogEvent?.("cancel");
  }

  // Styles
  static readonly styles = css`
    /* Override jha-dialog header padding via CSS custom properties */
    :host {
      --jha-card-header-padding-top: 18px;
      --jha-card-header-padding-bottom: 2px;
      --jha-card-header-padding-left: 14px;
      --jha-card-header-padding-right: 14px;
    }

    /* Allow dialog content to scroll so tooltips can be viewed */
    article[slot="dialog-content"] {
      overflow-y: auto !important;
      overflow-x: visible !important;
    }

    jha-dialog {
      --jha-dialog-overflow: auto;
    }

    jha-dialog::part(content),
    jha-dialog::part(card),
    jha-dialog::part(body) {
      overflow-y: auto !important;
      overflow-x: visible !important;
    }

    .generate-workflow-form {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 900px;
      min-width: 0;
      min-height: 200px;
      max-height: calc(80vh - 120px);
      overflow: visible; /* Allow tooltips to show outside container */
      padding-bottom: 0;
    }

    .form-header {
      margin-bottom: 3px;
      padding-bottom: 12px;
      flex-shrink: 0;
      overflow: visible; /* Allow tooltips to escape header */
      position: relative;
      z-index: 100; /* Ensure header is above scroll content but below tooltips */
    }

    .form-footer {
      margin-top: 0px;      
      padding-top: 20px;
      flex-shrink: 0;
      height: 2px;
    }

    .button-container {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-top: 1px;
      padding-top: 1px;
    }

    .instructions {
      margin: 0 0 4px 0;
      color: inherit;
      font-size: 13px;
      line-height: 1.4;
      opacity: 0.85;
    }

    .icon-instructions {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-top: 0;
      font-size: 11px;
      opacity: 0.7;
      line-height: 1.3;
    }

    .icon-instructions .icon-hint {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .icon-instructions jha-icon-document,
    .icon-instructions jha-icon-information,
    .icon-instructions jha-icon-code {
      font-size: 13px;
      opacity: 1;
    }
    
    .icon-instructions jha-icon-document {
      color: #4a9eff !important;
      --jha-icon-color: #4a9eff;
    }
    
    .icon-instructions jha-icon-information {
      color: #9c27b0 !important;
      --jha-icon-color: #9c27b0;
    }
    
    .icon-instructions jha-icon-code {
      color: #ff9800 !important;
      --jha-icon-color: #ff9800;
    }

    /* Scrollable container for many variables */
    .variables-scroll-container {
      overflow-y: auto;
      overflow-x: hidden;
      max-height: calc(70vh - 260px);
      padding: 10px 16px;
      margin-top: 4px;
      margin-bottom: 5px;
      flex: 1;
      min-height: 0;
      min-width: 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    /*
     * Variables: each pair renders as two grid rows — all labels align to one baseline row,
     * then all controls — so mismatched wrapped label heights do not vertically offset sibling inputs.
     */
    .variables-grid {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 2px 0;
      position: relative;
      min-width: 0;
    }

    .variable-grid-pair {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      grid-template-rows: auto auto;
      grid-template-areas:
        'lab-left lab-right'
        'fld-left fld-right';
      column-gap: 12px;
      row-gap: 4px;
      padding-top: 5px;
      min-width: 0;
    }

    .variable-label-cell--pair-left {
      grid-area: lab-left;
    }

    .variable-label-cell--pair-right {
      grid-area: lab-right;
    }

    .variable-field-cell--pair-left {
      grid-area: fld-left;
    }

    .variable-field-cell--pair-right {
      grid-area: fld-right;
    }

    /* Holds grid areas when a pair has only a left-column variable — keeps columns 50/50, no spanning. */
    .variable-label-cell--empty,
    .variable-field-cell--empty {
      min-height: 0;
    }

    /* Single column layout: stacked pairs (each variable label then control in reading order). */
    @media (max-width: 768px) {
      .variable-grid-pair {
        grid-template-columns: minmax(0, 1fr);
        grid-template-areas:
          'lab-left'
          'fld-left'
          'lab-right'
          'fld-right';
      }
    }

    .variable-field-cell {
      display: flex;
      flex-direction: column;
      gap: 0.5px;
      line-height: 1.25;
      min-width: 0;
    }

    .variable-label-cell {
      align-self: start;
      line-height: 1.25;
      min-width: 0;
    }

    .variable-label-cell label {
      display: flex;
      align-items: center;
      gap: 5px;
      font-weight: 500;
      font-size: 12px;
      color: inherit;
      margin: 0 0 0.5px 0;
      line-height: 1.25;
      min-width: 0;
    }

    .variable-label-cell label .variable-name {
      color: inherit;
      min-width: 0;
    }

    /* Icon styling - clickable with visual feedback */
    .variable-label-cell label jha-icon-document,
    .variable-label-cell label jha-icon-information,
    .variable-label-cell label jha-icon-code {
      cursor: pointer;
      opacity: 0.85;
      font-size: 14px;
      transition: all 0.2s ease;
      flex-shrink: 0;
    }

    .variable-label-cell label jha-icon-document {
      color: #4a9eff !important;
      --jha-icon-color: #4a9eff;
    }

    .variable-label-cell label jha-icon-information {
      color: #9c27b0 !important;
      --jha-icon-color: #9c27b0;
    }

    .variable-label-cell label jha-icon-code {
      color: #ff9800 !important;
      --jha-icon-color: #ff9800;
    }

    .variable-label-cell label jha-icon-document:hover,
    .variable-label-cell label jha-icon-information:hover,
    .variable-label-cell label jha-icon-code:hover {
      opacity: 1;
      transform: translateY(-1px);
    }

    .variable-label-cell label jha-icon-document:active,
    .variable-label-cell label jha-icon-information:active,
    .variable-label-cell label jha-icon-code:active {
      transform: scale(0.95) translateY(0);
    }

    .error-message {
      color: var(--jha-color-danger, #d32f2f);
      font-size: 12px;
      margin-top: 5px;
      line-height: 1.2;
    }

    .no-variables {
      text-align: center;
      padding: 32px;
      color: inherit;
      opacity: 0.7;
      font-size: 14px;
    }

    /*
     * Variable fields: jha-form-input and jha-form-select both use jha-form-floating-group internally.
     * Set the same design tokens on each host so text inputs and the region dropdown match (height, padding, font).
     * (jha-form-input does not expose ::part(input) in this WC version — variables must be used.)
     */
    .variable-field-cell jha-form-input,
    .variable-field-cell jha-form-select.variable-field-select {
      width: 100%;
      box-sizing: border-box;
      --jha-input-height: 40px;
      --jha-input-font-size: 14px;
      --jha-input-font-weight: 400;
      --jha-form-floating-group-padding-top: 3px;
      --jha-form-floating-group-input-line-height: 20px;
      --jha-text-input-margin-bottom: 0;
    }

    .variable-field-cell jha-form-input {
      --jha-input-padding: 8px 12px;
    }

    .variable-field-cell jha-form-select.variable-field-select {
      --jha-select-text: var(--jha-text-dark, inherit);
      /* Same vertical padding as inputs; extra right padding for the chevron icon. */
      --jha-input-padding: 8px 40px 8px 12px;
      --jha-input-margin-bottom: 0;
    }

    .variable-field-cell .tooltip-trigger.field-trigger {
      display: block;
      width: 100%;
      min-width: 0;
      align-self: stretch;
    }

    .variable-label-cell label .tooltip-trigger {
      vertical-align: middle;
    }

    /* Variable label icons — clickable */
    .variable-label-cell jha-icon-document,
    .variable-label-cell jha-icon-information,
    .variable-label-cell jha-icon-code {
      cursor: pointer;
    }

    /* ========================================
     * 🧪 DEV HELPER STYLES
     * Auto-fill button for development/testing
     * Only visible when showDevHelpers is true
     * (localhost + dev mode)
     * ======================================== */
    .test-autofill-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
      transition: all 0.2s ease;
      margin-left: auto;
    }

    .test-autofill-btn:hover {
      background: linear-gradient(135deg, #e55a2b 0%, #d4800a 100%);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(255, 107, 53, 0.4);
    }

    .test-autofill-btn:active {
      transform: translateY(0);
    }

    .test-autofill-btn jha-icon-wand-magic-sparkles {
      font-size: 14px;
    }

    .test-badge {
      background: #ff4444;
      color: white;
      font-size: 9px;
      padding: 2px 4px;
      border-radius: 3px;
      text-transform: uppercase;
      font-weight: 600;
    }

    .header-with-autofill {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
      overflow: visible; /* Allow tooltips to escape */
      position: relative;
    }

    .bu-misconfig-banner {
      margin: 0 0 12px 0;
      padding: 10px 12px;
      border-radius: 6px;
      font-size: 13px;
      line-height: 1.45;
      background: var(--jha-color-danger-tint, rgba(201, 37, 45, 0.12));
      border: 1px solid var(--jha-color-danger, #c9252d);
      color: inherit;
    }

    /* ============================================================
     * PORTAL TOOLTIP
     * Rendered at the dialog root with position:fixed so it cannot
     * be clipped by ancestor overflow (variables scroll container,
     * the dialog body, or the form-floating-group around selects).
     * ============================================================ */
    .tooltip-trigger {
      display: inline-flex;
      align-items: center;
      cursor: pointer;
    }

    .tooltip-trigger.field-trigger {
      display: block;
      width: 100%;
      cursor: default;
    }

    .custom-tooltip {
      position: fixed;
      z-index: 100000;
      max-width: 320px;
      padding: 8px 12px;
      background: var(--jha-tooltip-color-background, rgba(39, 45, 51, 0.95));
      color: var(--jha-tooltip-text-color, #ffffff);
      font-size: 12px;
      font-weight: 400;
      line-height: 1.5;
      text-align: left;
      border-radius: 4px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
      pointer-events: none;
      white-space: normal;
      word-wrap: break-word;
      animation: tooltip-fade-in 0.12s ease-out;
    }

    @keyframes tooltip-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .custom-tooltip::before {
      content: "";
      position: absolute;
      border: 6px solid transparent;
    }

    /* arrow points back at the trigger; tooltip side names where the tooltip sits */
    .custom-tooltip.side-right::before {
      right: 100%;
      top: 50%;
      transform: translateY(-50%);
      border-right-color: var(--jha-tooltip-color-background, rgba(39, 45, 51, 0.95));
    }
    .custom-tooltip.side-left::before {
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
      border-left-color: var(--jha-tooltip-color-background, rgba(39, 45, 51, 0.95));
    }
    .custom-tooltip.side-bottom::before {
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border-bottom-color: var(--jha-tooltip-color-background, rgba(39, 45, 51, 0.95));
    }
    .custom-tooltip.side-top::before {
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border-top-color: var(--jha-tooltip-color-background, rgba(39, 45, 51, 0.95));
    }
  `;

  /**
   * Get a stable key for a variable (variableId if available, otherwise name)
   * This handles cases where variableId is undefined (it's an optional property)
   */
  private getVariableKey(variable: WorkflowTemplateVariable): string {
    return variable.variableId ?? variable.name;
  }

  /**
   * Handle variable value change
   */
  private onVariableValueChange(variable: WorkflowTemplateVariable, e: Event) {
    const input = e.target as HTMLInputElement;
    const value = input.value ?? "";
    const key = this.getVariableKey(variable);

    // Update value
    this.variableValues = {
      ...this.variableValues,
      [key]: value,
    };

    // Validate in real-time
    const error = this.validateVariable(variable, value);
    if (error) {
      this.validationErrors = {
        ...this.validationErrors,
        [key]: error,
      };
    } else {
      // Remove error if valid
      const newErrors = { ...this.validationErrors };
      delete newErrors[key];
      this.validationErrors = newErrors;
    }
  }

  /**
   * Validate a single variable
   * Note: Values are trimmed before validation to ensure consistent behavior
   */
  private validateVariable(variable: WorkflowTemplateVariable, value: string): string | null {
    // Trim value for consistent validation
    const trimmedValue = (value || "").trim();

    // Built-in S3 Remote region: must be chosen from the same AWS region list as workflow steps (no free text).
    if (isBuiltinRegionVariableName(variable.name)) {
      if (this.awsRegionsLoading && this.awsRegionOptions.length === 0) {
        return "Loading AWS regions…";
      }
      if (trimmedValue === "") {
        return `{${variable.name}} is required`;
      }
      if (!this.isBuiltinRegionValueAllowed(trimmedValue)) {
        return "Select a valid AWS region from the list.";
      }
      return null;
    }

    // Built-in Business Unit: whitelist-validated against the option list built for the
    // current role and template category. No regex applied.
    if (isBuiltinBusUnitVariableName(variable.name)) {
      if (this.isBuScopedTemplateMissingPinnedBu) {
        return WorkflowTemplateGenerateDialog.BU_SCOPED_MISSING_PINNED_MESSAGE;
      }
      // Non-BUA + Global: wait for the active BU list before validating against it.
      if (
        !this.isBusinessUnitAdmin &&
        this.businessUnitsLoading &&
        this.activeBusinessUnitOptions.length === 0 &&
        this.isGlobalTemplate
      ) {
        return "Loading Business Units…";
      }
      if (trimmedValue === "") {
        return `{${variable.name}} is required`;
      }
      if (!this.isBuiltinBusUnitValueAllowed(trimmedValue)) {
        return "Select a valid Business Unit from the list.";
      }
      return null;
    }

    // Check required (all variables are required for Generate Workflow)
    if (trimmedValue === "") {
      return `{${variable.name}} is required`;
    }

    // Special validation for workflow name variables - no slashes allowed
    // Workflow names become part of S3 paths and cannot contain path separators
    const nameLower = variable.name.toLowerCase();
    if (nameLower === 'workflowname' || nameLower === 'workflow_name') {
      if (trimmedValue.includes('/') || trimmedValue.includes('\\')) {
        return "Workflow name cannot contain slashes (/ or \\)";
      }
    }

    if (nameLower === 'connectionstring') {
      if (!isValidAzureStorageConnectionString(trimmedValue)) {
        return AZURE_CONNECTION_STRING_FORMAT_WARNING;
      }
    }

    if (nameLower === 'rolearn' || nameLower === 'role_arn') {
      if (!isValidAwsIamRoleArn(trimmedValue)) {
        return AWS_IAM_ROLE_ARN_FORMAT_WARNING;
      }
    }

    // Check regex if provided (validate trimmed value)
    // Also trim the regex pattern in case it has leading/trailing whitespace
    if (variable.regexRule) {
      try {
        const trimmedRegex = variable.regexRule.trim();
        if (trimmedValue.length > REGEX_RULE_MAX_TEST_VALUE_LENGTH) {
          return "Value exceeds the maximum length allowed for this field";
        }
        const regex = new RegExp(trimmedRegex);
        const testResult = regex.test(trimmedValue);
        if (!testResult) {
          return variable.ruleDescription || "Invalid format";
        }
      } catch (error) {
        // Invalid regex syntax on the template variable (not the value the user typed here)
        return INVALID_TEMPLATE_REGEX_SYNTAX_MESSAGE;
      }
    }

    return null; // Valid
  }

  /**
   * Validate the entire form
   * Returns true if all variables have valid values
   */
  public isValid(): boolean {
    const errors: Record<string, string> = {};
    let isValid = true;

    this.variables.forEach((variable) => {
      const key = this.getVariableKey(variable);
      const value = this.variableValues[key] || "";
      const error = this.validateVariable(variable, value);
      if (error) {
        errors[key] = error;
        isValid = false;
      }
    });

    this.validationErrors = errors;
    this.requestUpdate();

    return isValid;
  }

  /**
   * Check if the form can be submitted (all fields valid)
   * This is a lightweight check that doesn't update validation errors - used for button state
   */
  private canSubmit(): boolean {
    // If there are no variables, cannot submit
    if (this.variables.length === 0) {
      return false;
    }

    // Check each variable without updating error state
    for (const variable of this.variables) {
      const key = this.getVariableKey(variable);
      const value = this.variableValues[key] || "";
      const error = this.validateVariable(variable, value);
      if (error) {
        return false;
      }
    }

    return true;
  }

  /**
   * Get collected variable values
   * Returns a map of Name -> value (values are trimmed)
   */
  public getValues(): Record<string, string> {
    const values: Record<string, string> = {};

    this.variables.forEach((variable) => {
      const key = this.getVariableKey(variable);
      // Trim values to ensure clean data is sent to API
      values[variable.name] = (this.variableValues[key] || "").trim();
    });

    return values;
  }

  /**
   * Called by DialogUtil when user clicks the confirm button
   * Must validate and return the data to pass back
   */
  async getDialogData() {
    // Validate all fields
    if (!this.isValid()) {
      showToastMessage("Please fix validation errors before proceeding.", "danger");
      throw new Error("Validation failed");
    }

    // Return the variable values
    return this.getValues();
  }

  /**
   * Copy text to clipboard and show feedback
   */
  private async copyToClipboard(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      showToastMessage(`${label} copied to clipboard`, "success");
    } catch (error) {
      showToastMessage("Failed to copy to clipboard", "danger");
    }
  }

  /**
   * Handle click on description icon - copy description to clipboard
   */
  private onDescriptionIconClick(e: Event, variable: WorkflowTemplateVariable) {
    e.stopPropagation();
    e.preventDefault();
    if (variable.description) {
      this.copyToClipboard(variable.description, "Description");
    }
  }

  /**
   * Handle click on regex description icon - copy regex description to clipboard
   */
  private onRegexDescriptionIconClick(e: Event, variable: WorkflowTemplateVariable) {
    e.stopPropagation();
    e.preventDefault();
    if (variable.ruleDescription) {
      this.copyToClipboard(variable.ruleDescription, "Regex description");
    }
  }

  /**
   * Handle click on regex icon - copy raw regex pattern to clipboard
   */
  private onRegexIconClick(e: Event, variable: WorkflowTemplateVariable) {
    e.stopPropagation();
    e.preventDefault();
    if (variable.regexRule) {
      this.copyToClipboard(variable.regexRule, "Regex pattern");
    }
  }

  // ========================================
  // 🧪 DEVELOPMENT HELPER METHODS
  // ========================================
  // These methods support the auto-fill button that appears only in local dev mode.
  // They generate valid test data for variables based on their names and regex patterns.
  // 
  // The code ships to production but never executes because:
  // 1. showDevHelpers returns false in production builds
  // 2. The auto-fill button is not rendered
  // 3. onAutoFillClick is never called
  //
  // See showDevHelpers getter for full documentation on this approach.
  // ========================================

  /**
   * 🧪 DEV HELPER: Pick random item from array
   * Used to add variety to auto-generated test values
   */
  private pickRandom<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  /**
   * 🧪 DEV HELPER: Generate unique timestamp suffix (ensures uniqueness)
   * Used to ensure auto-generated workflow names don't collide
   */
  private uniqueSuffix(): string {
    return `${Date.now()}_${Math.floor(Math.random() * 10000)}`;
  }

  /**
   * 🧪 DEV HELPER: Generate valid test value for a variable based on its regex
   * 
   * This method uses heuristics to generate appropriate test values:
   * 1. Special handling for common variable names (workflowName, hostname, port, etc.)
   * 2. Regex pattern analysis for unknown variables
   * 3. Fallback to safe alphanumeric values
   * 
   * The generated values are designed to pass regex validation while being
   * recognizable as test data (prefixed with "test", "wftest", etc.)
   */
  private generateTestValueForVariable(variable: WorkflowTemplateVariable): string {
    const name = variable.name.toLowerCase();
    const originalName = variable.name;

    // ============================================================
    // ALWAYS UNIQUE FIELDS - workflowName and sourceLocationPath
    // These MUST be unique for every generated workflow
    // ============================================================
    if (name === 'workflowname' || name === 'workflow_name') {
      const prefixes = [
        'DailyTransfer', 'BatchSync', 'FileArchive', 'DataMigration', 'NightlyETL',
        'HourlySync', 'WeeklyBackup', 'MonthlyReport', 'RealTimeIngest', 'StreamProcess',
        'BulkLoad', 'IncrementalSync', 'FullExport', 'DeltaProcess', 'SnapshotCapture',
        'LogShipper', 'MetricsPush', 'EventRelay', 'AlertForward', 'AuditArchive',
        'ComplianceExport', 'ReconcileJob', 'ValidationRun', 'CleanupTask', 'PurgeJob',
        'ReplicationSync', 'FailoverCopy', 'DisasterRecovery', 'GeoSync', 'CrossRegion',
        'PartnerFeed', 'VendorImport', 'ClientExport', 'CoreBankingSync', 'LoanProcessor',
        'PaymentRelay', 'ACHBatch', 'WireTransfer', 'FedFileSync', 'RegReporting'
      ];
      return `wftestname_${this.pickRandom(prefixes)}_${this.uniqueSuffix()}`;
    }

    if (name === 'sourcelocationpath' || name === 'source_location_path' || name === 'sourcelocation') {
      const bases = [
        'incoming', 'uploads', 'raw', 'staging', 'landing', 'inbound', 'external',
        'partner', 'vendor', 'client', 'feed', 'stream', 'batch', 'queue', 'pending'
      ];
      const subs = [
        'daily', 'hourly', 'realtime', 'archive', 'current', 'latest', 'new', 'processing'
      ];
      return `/${this.pickRandom(bases)}/${this.pickRandom(subs)}/${this.uniqueSuffix()}/`;
    }

    if (isBuiltinRegionVariableName(originalName)) {
      if (this.awsRegionOptions.length > 0) {
        return this.pickRandom(this.awsRegionOptions.map((o) => o.value));
      }
      return "us-east-1";
    }

    if (isBuiltinBusUnitVariableName(originalName)) {
      const auto = this.computeAutoSelectedBuValue();
      if (auto) return auto;
      const options = this.buildBusinessUnitOptionsForUi();
      if (options.length > 0) return options[0].value;
      return "";
    }

    const builtinS3RegionDevValues = [
      'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
      'eu-west-1', 'eu-west-2', 'eu-west-3', 'eu-central-1', 'eu-north-1',
      'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1', 'ap-northeast-2', 'ap-south-1',
      'sa-east-1', 'ca-central-1', 'me-south-1', 'af-south-1',
    ];

    // Known variable patterns with ARRAYS of test values for variety (100+ combinations)
    const testValueOptions: Record<string, string[]> = {
      // AWS/Cloud - expanded
      'aws_region': [
        'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
        'eu-west-1', 'eu-west-2', 'eu-west-3', 'eu-central-1', 'eu-north-1',
        'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1', 'ap-northeast-2', 'ap-south-1',
        'sa-east-1', 'ca-central-1', 'me-south-1', 'af-south-1'
      ],
      // Built-in S3 Remote region (BUILTIN_REGION_VARIABLE)
      builtin_region_variable: builtinS3RegionDevValues,
      'region': [
        'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
        'eu-west-1', 'eu-west-2', 'eu-central-1', 'eu-north-1',
        'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1', 'ap-south-1',
        'sa-east-1', 'ca-central-1'
      ],
      'destination_bucket': [
        'dest-bucket-prod', 'archive-bucket', 'output-files-bucket', 'processed-data',
        'final-output', 'completed-jobs', 'success-archive', 'delivery-zone',
        'export-ready', 'outbound-files', 'partner-delivery', 'client-drop',
        'backup-primary', 'backup-secondary', 'cold-storage', 'glacier-archive'
      ],
      'source_bucket': [
        'source-bucket-prod', 'incoming-files', 'raw-data-bucket', 'upload-bucket',
        'landing-zone', 'ingest-bucket', 'staging-area', 'hot-inbound',
        'partner-uploads', 'vendor-feeds', 'client-submissions', 'external-data',
        'ftp-landing', 'sftp-incoming', 'api-uploads', 'stream-buffer'
      ],
      'bucketpath1': [
        'incoming/files', 'data/raw', 'uploads/daily', 'transfers/batch',
        'feeds/partner', 'ingest/realtime', 'staging/validation', 'queue/pending',
        'archive/2024', 'archive/2025', 'archive/2026', 'backup/full',
        'logs/application', 'logs/audit', 'metrics/daily', 'reports/monthly',
        'client/acme', 'client/globex', 'vendor/supplier1', 'partner/banknet'
      ],
      'bucketpathpart2': [
        'processed', 'archive', 'completed', 'output', 'success', 'validated',
        'transformed', 'enriched', 'cleansed', 'normalized', 'aggregated',
        'final', 'ready', 'approved', 'verified', 'certified'
      ],
      'bucketname': [
        'workflow-data-bucket', 'file-transfer-bucket', 'etl-staging', 'data-pipeline',
        'enterprise-files', 'core-data-lake', 'analytics-raw', 'bi-staging',
        'compliance-archive', 'audit-logs', 'transaction-history', 'customer-data',
        'product-catalog', 'inventory-sync', 'order-processing', 'payment-records'
      ],
      'arn': [
        'arn:aws:iam::123456789012:role/WorkflowRole',
        'arn:aws:iam::987654321098:role/DataTransfer',
        'arn:aws:iam::111222333444:role/FileProcessor',
        'arn:aws:iam::555666777888:role/S3ReadWrite',
        'arn:aws:iam::999000111222:role/CrossAccountSync',
        'arn:aws:iam::333444555666:role/LambdaExecutor',
        'arn:aws:iam::777888999000:role/ETLRunner',
        'arn:aws:iam::222333444555:role/BatchProcessor'
      ],
      'rolearn': [
        'arn:aws:iam::123456789012:role/CrossAccountRole',
        'arn:aws:iam::555666777888:role/S3Access',
        'arn:aws:iam::999000111222:role/RemoteAccess',
        'arn:aws:iam::111222333444:role/AssumeRoleTarget',
        'arn:aws:iam::444555666777:role/PartnerAccess',
        'arn:aws:iam::888999000111:role/VendorSync',
        'arn:aws:iam::666777888999:role/ExternalIntegration'
      ],

      // Step naming - expanded
      'stepname': [
        'CopyToArchive', 'MoveToProcessed', 'TransferToRemote', 'BackupFiles', 'ValidateData',
        'EncryptFiles', 'DecryptFiles', 'CompressArchive', 'ExtractZip', 'SplitLargeFile',
        'MergeChunks', 'ConvertFormat', 'TransformXML', 'ParseJSON', 'ValidateSchema',
        'ChecksumVerify', 'VirusScan', 'QuarantineFile', 'NotifySuccess', 'NotifyFailure',
        'CleanupTemp', 'ArchiveOriginal', 'DeleteProcessed', 'RetryFailed', 'EscalateError'
      ],
      'loctype': ['internal', 'external', 'partner', 'client', 'vendor', 'dmz', 'secure', 'public'],
      'locationtype': ['internal', 'external', 'dmz', 'secure', 'restricted', 'public', 'private', 'hybrid'],

      // File patterns - expanded
      'amazons3bucketpattern': [
        '*.csv', '*.txt', '*.json', '*.xml', '*.xlsx', '*.pdf', '*.zip', '*.gz',
        'data_*.csv', 'export_*.xlsx', 'report_*.pdf', 'feed_*.json', 'batch_*.dat',
        'ACH_*.txt', 'WIRE_*.xml', 'FED_*.dat', 'NACHA_*.txt', 'ISO20022_*.xml',
        '*_daily.csv', '*_hourly.json', '*_monthly.xlsx', '*_annual.pdf'
      ],
      'filepattern': [
        '*.txt', '*.csv', '*.dat', '*.json', '*.xml', '*.xlsx', '*.pdf', '*.zip',
        'report_*.pdf', 'log_*.txt', 'data_*.csv', 'feed_*.json', 'export_*.xlsx',
        'transaction_*.dat', 'balance_*.csv', 'statement_*.pdf', 'audit_*.log'
      ],
      'folderpattern': [
        'data/incoming', 'files/upload', 'batch/daily', 'sync/hourly',
        'feeds/realtime', 'archive/monthly', 'backup/weekly', 'staging/validation',
        'processed/success', 'processed/failed', 'quarantine/review', 'temp/working'
      ],
      'wfdfp': [
        'workflow_data', 'batch_files', 'daily_export', 'sync_output', 'transfer_pkg',
        'etl_results', 'transform_out', 'validated_set', 'approved_batch', 'final_delivery',
        'partner_feed', 'client_report', 'vendor_import', 'core_extract', 'gl_posting'
      ],

      // Connection/Credentials - expanded
      'host': [
        'sftp.example.com', 'ftp.partner.net', 'files.vendor.io', 'transfer.client.org',
        'secure.banknet.com', 'feeds.fintech.io', 'data.processor.net', 'sync.cloudhost.com',
        'sftp.acmecorp.com', 'ftp.globexinc.net', 'files.initech.io', 'transfer.umbrella.org',
        'secure.wayneent.com', 'feeds.starkindustries.io', 'data.lexcorp.net'
      ],
      'hostname': [
        'ftp.fileserver.net', 'sftp.datahost.com', 'secure.transfers.io', 'files.acme.corp',
        'batch.processor.net', 'sync.cloudprovider.com', 'feeds.datavendor.io', 'etl.warehouse.net',
        'sftp.bigbank.com', 'ftp.creditunion.net', 'files.insurance.io', 'transfer.broker.org'
      ],
      'port': ['21', '22', '990', '2222', '8021', '8022', '10022', '20021', '20022'],
      'username': [
        'svc_workflow', 'file_transfer', 'etl_user', 'batch_svc', 'data_sync',
        'sftp_user', 'ftp_service', 'integration_svc', 'partner_account', 'vendor_feed',
        'client_upload', 'external_sync', 'internal_batch', 'scheduled_job', 'automated_task'
      ],
      'password': [
        'SecureP@ss123', 'Tr@nsfer2024!', 'D@taSync99', 'F1leM0ve#', 'B@tchRun456',
        'Integr@te789', 'Workfl0w!2024', 'Autom@te321', 'Pr0cess!ng', 'S3cur3D@ta'
      ],
      'connectionstring': [
        'DefaultEndpointsProtocol=https;AccountName=prodstore;AccountKey=Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw==;EndpointSuffix=core.windows.net',
        'DefaultEndpointsProtocol=https;AccountName=devdata;AccountKey=Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw==;EndpointSuffix=core.windows.net',
        'DefaultEndpointsProtocol=https;AccountName=archiveblob;AccountKey=Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw==',
        'DefaultEndpointsProtocol=https;AccountName=stagingfiles;AccountKey=Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw==',
        'DefaultEndpointsProtocol=https;AccountName=backupaccount;AccountKey=Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw==;EndpointSuffix=core.windows.net',
        'DefaultEndpointsProtocol=https;AccountName=disasterrecov;AccountKey=Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw==',
        'DefaultEndpointsProtocol=https;AccountName=partnershare;AccountKey=Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw==;EndpointSuffix=core.windows.net',
        'DefaultEndpointsProtocol=https;AccountName=clientdata;AccountKey=Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw=='
      ],

      // Business - expanded
      'email': [
        'workflow-alerts@company.com', 'data-ops@corp.net', 'file-notify@team.io',
        'etl-status@org.com', 'batch-monitor@enterprise.com', 'integration@firm.net',
        'it-alerts@bank.com', 'ops-notify@creditunion.org', 'support@fintech.io',
        'admin@processing.net', 'noc@datacenter.com', 'oncall@devops.io'
      ],
      'email_notification': [
        'admin@example.com', 'ops-team@company.com', 'alerts@monitoring.net',
        'support@helpdesk.com', 'escalation@oncall.io', 'management@reports.net',
        'compliance@audit.com', 'security@infosec.io', 'risk@oversight.net'
      ],
      'business_unit': [
        'Finance', 'Operations', 'IT', 'HR', 'Sales', 'Marketing',
        'Accounting', 'Treasury', 'Risk', 'Compliance', 'Legal', 'Audit',
        'Lending', 'Deposits', 'Payments', 'Cards', 'Wealth', 'Insurance',
        'Commercial', 'Retail', 'Corporate', 'Investment', 'Private'
      ],
      'fi_name': [
        'First National Bank', 'Metro Credit Union', 'Valley Savings', 'Coastal Bank',
        'Mountain View FCU', 'Riverside Community Bank', 'Harbor Financial', 'Prairie State Bank',
        'Sunset Credit Union', 'Northern Trust', 'Eastern Savings', 'Western Regional Bank',
        'Central Plains CU', 'Pacific Coast Bank', 'Atlantic Federal', 'Midwest Community'
      ],
      'priority': ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
      'environment': ['dev', 'development', 'staging', 'uat', 'qa', 'preprod', 'prod', 'production', 'dr', 'sandbox'],

      // Numeric - expanded
      'max_file_size_mb': ['50', '100', '250', '500', '1000', '2048', '4096', '8192', '10240'],
      'retention_days': ['7', '14', '30', '60', '90', '180', '365', '730', '1095', '2555'],
      'timeout_seconds': ['60', '120', '180', '300', '600', '900', '1200', '1800', '3600', '7200'],
      'retry_count': ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
    };

    // Check if we have predefined test values (case-insensitive)
    if (testValueOptions[name]) {
      return this.pickRandom(testValueOptions[name]);
    }

    // Fuzzy match on partial names
    for (const [key, values] of Object.entries(testValueOptions)) {
      if (name.includes(key) || key.includes(name)) {
        return this.pickRandom(values);
      }
    }

    // Fallback: try to generate based on common regex patterns
    if (variable.regexRule) {
      const regex = variable.regexRule;

      // Email pattern
      if (regex.includes('@') && regex.includes('\\.')) {
        return this.pickRandom(['test@example.com', 'user@domain.net', 'notify@alerts.io']);
      }
      // UUID pattern
      if (regex.includes('[a-f0-9]{8}') || regex.includes('uuid')) {
        const hex = () => Math.floor(Math.random() * 16).toString(16);
        const seg = (n: number) => Array(n).fill(0).map(hex).join('');
        return `${seg(8)}-${seg(4)}-${seg(4)}-${seg(4)}-${seg(12)}`;
      }
      // Number range patterns
      if (regex.match(/^\^?\[1-5\]\$?$/)) {
        return this.pickRandom(['1', '2', '3', '4', '5']);
      }
      if (regex.match(/^\^?\[0-5\]\$?$/)) {
        return this.pickRandom(['0', '1', '2', '3', '4', '5']);
      }
      // Simple word patterns
      if (regex.includes('dev|staging|prod')) {
        return this.pickRandom(['dev', 'staging', 'prod']);
      }
      if (regex.includes('batch|streaming')) {
        return this.pickRandom(['batch', 'streaming']);
      }
      if (regex.includes('DEBUG|INFO|WARN|ERROR')) {
        return this.pickRandom(['DEBUG', 'INFO', 'WARN', 'ERROR']);
      }
    }

    // Smart fallback based on name patterns
    if (name.includes('path') || name.includes('folder') || name.includes('dir')) {
      return this.pickRandom(['data/files', 'incoming/batch', 'output/daily', 'sync/archive']);
    }
    if (name.includes('bucket')) {
      return this.pickRandom(['my-bucket-name', 'data-bucket-01', 'file-storage', 'transfer-bucket']);
    }
    if (name.includes('name') || name.includes('id')) {
      const suffix = Math.floor(Math.random() * 900) + 100; // 100-999
      return `${originalName}-${suffix}`;
    }
    if (name.includes('pattern')) {
      return this.pickRandom(['*.txt', '*.csv', '*.json', 'data_*.xml', 'export_*.*']);
    }

    // Ultimate fallback - use original casing with random suffix
    const suffix = Math.floor(Math.random() * 9000) + 1000; // 1000-9999
    return `${originalName}_${suffix}`;
  }

  /**
   * 🧪 DEV HELPER: Validate a value against a regex pattern
   */
  private validateAgainstRegex(value: string, regexRule: string | undefined): boolean {
    if (!regexRule) return true;
    try {
      const trimmed = regexRule.trim();
      if ((value || "").length > REGEX_RULE_MAX_TEST_VALUE_LENGTH) return false;
      const regex = new RegExp(trimmed);
      return regex.test(value);
    } catch {
      return true; // If regex is invalid, assume value is ok
    }
  }

  /**
   * 🧪 DEV HELPER: Generate a safe alphanumeric fallback value
   * This is guaranteed to work with most regex patterns
   */
  private generateSafeFallback(): string {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    // Use only lowercase letters and numbers - most universally accepted
    return `test${timestamp}${random}`;
  }

  /**
   * 🧪 DEV HELPER: Generate a string that satisfies a regex pattern.
   *
   * Lightweight, dependency-free regex string generator. Parses a useful subset
   * of JS regex syntax and emits a matching string:
   *   - Literals & escapes: \d \w \s \. \\ \/ \- etc.
   *   - Character classes: [a-z], [A-Z0-9_], [^...] (negated)
   *   - Predefined: \d \D \w \W \s \S, .
   *   - Quantifiers: ?, *, +, {n}, {n,}, {n,m} (bounded; * and + clamped to small counts)
   *   - Anchors: ^ and $ are stripped (treated as positional, not consumed)
   *   - Groups: (...) and non-capturing (?:...)
   *   - Alternation: a|b|c (top-level inside a group)
   *
   * Returns null if the pattern uses constructs we can't safely synthesize
   * (lookarounds, backreferences, unbalanced groups, etc.). Caller should fall
   * back to brute-force candidates in that case.
   */
  private generateValueFromRegex(pattern: string): string | null {
    if (!pattern) return null;
    let raw = pattern.trim();
    // Strip a single set of leading/trailing slashes if a /pattern/flags form snuck in.
    const slashFlags = raw.match(/^\/(.*)\/([gimsuy]*)$/);
    if (slashFlags) raw = slashFlags[1];
    // Anchors are positional only — drop them so they don't appear as literals.
    if (raw.startsWith("^")) raw = raw.slice(1);
    if (raw.endsWith("$") && !raw.endsWith("\\$")) raw = raw.slice(0, -1);

    // Bail on unsupported features that would silently produce wrong output.
    if (/\(\?[=!<]/.test(raw)) return null; // lookarounds
    if (/\\\d/.test(raw)) return null; // backreferences

    let pos = 0;
    const pickRandom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
    const SAFE_ANY = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const DIGIT = "0123456789";
    const WORD = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_";
    const SPACE = " ";

    const expandRange = (a: string, b: string): string => {
      const start = a.charCodeAt(0);
      const end = b.charCodeAt(0);
      let out = "";
      for (let c = start; c <= end; c++) out += String.fromCharCode(c);
      return out;
    };

    const escapeChars = (esc: string): string => {
      switch (esc) {
        case "d": return DIGIT;
        case "w": return WORD;
        case "s": return SPACE;
        case "D": return "abcdefghijklmnopqrstuvwxyz";
        case "W": return "-_.";
        case "S": return "abc123";
        case "n": return "\n";
        case "r": return "\r";
        case "t": return "\t";
        default: return esc; // literal: . / \ - + * ? ( ) [ ] { } | ^ $
      }
    };

    const parseCharClass = (): string | null => {
      // pos is at '['
      pos++;
      let negated = false;
      if (raw[pos] === "^") { negated = true; pos++; }
      let chars = "";
      while (pos < raw.length && raw[pos] !== "]") {
        let c: string;
        if (raw[pos] === "\\" && pos + 1 < raw.length) {
          c = escapeChars(raw[pos + 1]);
          pos += 2;
        } else {
          c = raw[pos];
          pos++;
        }
        // Range like a-z
        if (raw[pos] === "-" && raw[pos + 1] && raw[pos + 1] !== "]") {
          let endC: string;
          if (raw[pos + 1] === "\\" && pos + 2 < raw.length) {
            endC = escapeChars(raw[pos + 2]);
            pos += 3;
          } else {
            endC = raw[pos + 1];
            pos += 2;
          }
          if (c.length === 1 && endC.length === 1) {
            chars += expandRange(c, endC);
          } else {
            chars += c + endC;
          }
        } else {
          chars += c;
        }
      }
      if (raw[pos] !== "]") return null;
      pos++; // consume ]
      if (negated) {
        const set = new Set(chars);
        let allowed = "";
        for (const ch of SAFE_ANY) if (!set.has(ch)) allowed += ch;
        chars = allowed || "x";
      }
      return chars;
    };

    const parseQuantifier = (): { min: number; max: number } | null => {
      if (pos >= raw.length) return null;
      const ch = raw[pos];
      if (ch === "?") { pos++; return { min: 0, max: 1 }; }
      if (ch === "*") { pos++; return { min: 0, max: 4 }; }
      if (ch === "+") { pos++; return { min: 1, max: 4 }; }
      if (ch === "{") {
        const close = raw.indexOf("}", pos);
        if (close < 0) return null;
        const body = raw.slice(pos + 1, close);
        pos = close + 1;
        const m = body.match(/^(\d+)(?:,(\d*))?$/);
        if (!m) return null;
        const min = parseInt(m[1], 10);
        const max = m[2] === undefined ? min : (m[2] === "" ? min + 4 : parseInt(m[2], 10));
        return { min, max: Math.max(min, max) };
      }
      return null;
    };

    // Recursively produce a string for the alternation/sequence at current pos
    // until we hit ')' or end of string.
    const parseAlternation = (): string | null => {
      const alternatives: string[] = [];
      let current = parseSequence();
      if (current === null) return null;
      alternatives.push(current);
      while (pos < raw.length && raw[pos] === "|") {
        pos++;
        const next = parseSequence();
        if (next === null) return null;
        alternatives.push(next);
      }
      return pickRandom(alternatives);
    };

    const parseSequence = (): string | null => {
      let out = "";
      while (pos < raw.length && raw[pos] !== ")" && raw[pos] !== "|") {
        let unit: string;
        const ch = raw[pos];
        if (ch === "(") {
          pos++; // consume (
          // optional non-capturing prefix (?:
          if (raw[pos] === "?" && raw[pos + 1] === ":") pos += 2;
          const inner = parseAlternation();
          if (inner === null) return null;
          if (raw[pos] !== ")") return null;
          pos++; // consume )
          unit = inner;
        } else if (ch === "[") {
          const cls = parseCharClass();
          if (cls === null) return null;
          unit = cls;
        } else if (ch === ".") {
          pos++;
          unit = SAFE_ANY;
        } else if (ch === "\\" && pos + 1 < raw.length) {
          unit = escapeChars(raw[pos + 1]);
          pos += 2;
        } else if (ch === "^" || ch === "$") {
          // Stray anchor; skip.
          pos++;
          continue;
        } else {
          unit = ch;
          pos++;
        }

        // Apply quantifier if present.
        const q = parseQuantifier();
        if (q) {
          const count = q.min + Math.floor(Math.random() * (q.max - q.min + 1));
          let piece = "";
          for (let i = 0; i < count; i++) {
            piece += unit.length === 1 ? unit : pickRandom(unit.split(""));
          }
          out += piece;
        } else {
          // No quantifier — pick one char if `unit` is a character set, else use literal/group result.
          if (unit.length > 1 && (raw[pos - 1] === "]" || raw[pos - 1] === "." ||
            (pos >= 2 && raw[pos - 2] === "\\"))) {
            // It came from a char class / . / escape that represents a set: pick one.
            out += pickRandom(unit.split(""));
          } else {
            out += unit;
          }
        }
      }
      return out;
    };

    try {
      const result = parseAlternation();
      if (result === null) return null;
      if (pos !== raw.length) return null;
      // Final sanity check: regex must actually match what we generated.
      const compiled = new RegExp(pattern.trim());
      if (!compiled.test(result)) return null;
      return result;
    } catch {
      return null;
    }
  }

  /**
   * 🧪 DEV HELPER: Find a value satisfying `regexRule` by trying multiple strategies.
   * Returns null if no strategy succeeds — caller should leave the field empty.
   */
  private findValueMatchingRegex(initialCandidate: string, regexRule: string): string | null {
    if (this.validateAgainstRegex(initialCandidate, regexRule)) return initialCandidate;

    // 1. Synthesize directly from the regex (up to a few attempts for randomized branches).
    for (let i = 0; i < 8; i++) {
      const generated = this.generateValueFromRegex(regexRule);
      if (generated !== null && this.validateAgainstRegex(generated, regexRule)) {
        return generated;
      }
    }

    // 2. Brute-force candidate library — covers patterns the parser declined.
    const candidates = [
      this.generateSafeFallback(),
      `${Date.now()}`,
      "testvalue",
      "test",
      "a",
      "1",
      "0",
      "test123",
      "Test123",
      "TEST",
      "test_value",
      "test-value",
      "test.value",
      "test@example.com",
      "/test/path/",
      "true",
      "false",
    ];
    for (const c of candidates) {
      if (this.validateAgainstRegex(c, regexRule)) return c;
    }
    return null;
  }

  /**
   * 🧪 DEV HELPER: Auto-fill all variables with valid test data
   * 
   * This is the main entry point for the auto-fill feature.
   * Called when the user clicks the auto-fill button (only visible in dev mode).
   * 
   * For each variable:
   * 1. Generates a test value using generateTestValueForVariable()
   * 2. Validates against regex if present
   * 3. Falls back to safer values if initial value fails validation
   * 4. Updates the form state
   */
  private async onAutoFillClick() {
    console.log('[DEV] 🧪 Auto-filling all variables with test data...');

    await this.ensureAwsRegionsLoaded();

    const newValues: Record<string, string> = {};
    let regexFailures = 0;

    this.variables.forEach((variable) => {
      const key = this.getVariableKey(variable);
      let testValue = this.generateTestValueForVariable(variable);

      if (variable.regexRule && !this.validateAgainstRegex(testValue, variable.regexRule)) {
        console.log(`[DEV] ⚠️ "${testValue}" failed regex "${variable.regexRule}" for ${variable.name} — synthesizing from regex`);
        const matched = this.findValueMatchingRegex(testValue, variable.regexRule);
        if (matched !== null) {
          testValue = matched;
          console.log(`[DEV] ✅ Regex-compliant value: "${testValue}"`);
        } else {
          regexFailures++;
          console.log(`[DEV] ❌ Could not find valid value for regex "${variable.regexRule}"`);
        }
      }

      newValues[key] = testValue;
      console.log(`[DEV] ${variable.name} = "${testValue}"`);
    });

    // Update all values at once
    this.variableValues = newValues;

    // Clear any validation errors
    this.validationErrors = {};

    // Trigger re-render
    this.requestUpdate();

    if (regexFailures > 0) {
      showToastMessage(`🧪 Auto-filled ${this.variables.length} variables (${regexFailures} may need manual adjustment)`, 'danger');
    } else {
      showToastMessage(`🧪 Auto-filled ${this.variables.length} variables with test data`, 'success');
    }
  }

  /**
   * Label row + icon tooltips for one variable.
   * @param variableIndex - grid order: even = left column (2-col), odd = right column; drives tooltip side to avoid scroll-area clipping
   */
  private buildVariableLabelRow(variable: WorkflowTemplateVariable, variableIndex: number) {
    const tipRight = this.labelTooltipOpensRight(variableIndex);

    const labelTipSide: "left" | "right" = tipRight ? "right" : "left";
    return html`
      <label>
        <span class="variable-name">{${variable.name}}</span>
        ${variable.description
        ? html`
              <span
                class="tooltip-trigger"
                @mouseenter=${(e: Event) =>
            this.showCustomTooltip(e, variable.description, labelTipSide)}
                @mouseleave=${() => this.hideCustomTooltip()}
                @focusin=${(e: Event) =>
            this.showCustomTooltip(e, variable.description, labelTipSide)}
                @focusout=${() => this.hideCustomTooltip()}>
                <jha-icon-document
                  data-auto-id="GenerateDialogDescriptionIcon"
                  @click=${(e: Event) => this.onDescriptionIconClick(e, variable)}></jha-icon-document>
              </span>
            `
        : ""}
        ${variable.ruleDescription
        ? html`
              <span
                class="tooltip-trigger"
                @mouseenter=${(e: Event) =>
            this.showCustomTooltip(e, variable.ruleDescription!, labelTipSide)}
                @mouseleave=${() => this.hideCustomTooltip()}
                @focusin=${(e: Event) =>
            this.showCustomTooltip(e, variable.ruleDescription!, labelTipSide)}
                @focusout=${() => this.hideCustomTooltip()}>
                <jha-icon-information
                  data-auto-id="GenerateDialogRegexDescriptionIcon"
                  @click=${(e: Event) =>
            this.onRegexDescriptionIconClick(e, variable)}></jha-icon-information>
              </span>
            `
        : ""}
        ${variable.regexRule
        ? html`
              <span
                class="tooltip-trigger"
                @mouseenter=${(e: Event) =>
            this.showCustomTooltip(e, variable.regexRule!, labelTipSide)}
                @mouseleave=${() => this.hideCustomTooltip()}
                @focusin=${(e: Event) =>
            this.showCustomTooltip(e, variable.regexRule!, labelTipSide)}
                @focusout=${() => this.hideCustomTooltip()}>
                <jha-icon-code
                  data-auto-id="GenerateDialogRegexIcon"
                  @click=${(e: Event) => this.onRegexIconClick(e, variable)}></jha-icon-code>
              </span>
            `
        : ""}
      </label>
    `;
  }

  /** Form control + validation messages (labels are rendered in a separate grid row). */
  private renderVariableFieldContent(variable: WorkflowTemplateVariable) {
    const key = this.getVariableKey(variable);
    const hasError = !!this.validationErrors[key];
    const errorMessage = this.validationErrors[key];

    if (isBuiltinRegionVariableName(variable.name)) {
      const regionTip = "Select an AWS region from the list.";
      return html`
        <span
          class="tooltip-trigger field-trigger"
          @mouseenter=${(e: Event) => this.showCustomTooltip(e, regionTip, "bottom")}
          @mouseleave=${() => this.hideCustomTooltip()}
          @focusin=${(e: Event) => this.showCustomTooltip(e, regionTip, "bottom")}
          @focusout=${() => this.hideCustomTooltip()}>
          <jha-form-select
            class="variable-field-select"
            required
            .warning=${hasError ? errorMessage : ""}
            ?showWarning=${hasError}
            .options=${this.awsRegionOptions}
            labelKey="name"
            valueKey="value"
            data-id="BuiltinRegionVariable"
            data-auto-id="GenerateDialogRegionSelect"
            .value=${this.variableValues[key] || ""}
            @change=${(e: Event) => this.onVariableValueChange(variable, e)}
            ?disabled=${this.awsRegionsLoading}
            isValid
            emptyOptionLabel=${this.awsRegionsLoading ? "Loading regions…" : "Select region"}
          >
          </jha-form-select>
        </span>
        ${hasError ? html` <div class="error-message">${errorMessage}</div> ` : ""}
      `;
    }

    if (isBuiltinBusUnitVariableName(variable.name)) {
      const buMisconfigured = this.isBuScopedTemplateMissingPinnedBu;
      const buOptions = this.buildBusinessUnitOptionsForUi();
      const isLocked = this.busUnitDropdownIsLocked;
      const isLoading =
        !this.isBusinessUnitAdmin &&
        this.businessUnitsLoading &&
        this.activeBusinessUnitOptions.length === 0 &&
        this.isGlobalTemplate;
      const forcedBuError = buMisconfigured
        ? WorkflowTemplateGenerateDialog.BU_SCOPED_MISSING_PINNED_MESSAGE
        : null;
      const effectiveError = forcedBuError || (hasError ? errorMessage : "");
      const showBuWarning = !!forcedBuError || hasError;
      const buTip = buMisconfigured
        ? WorkflowTemplateGenerateDialog.BU_SCOPED_MISSING_PINNED_MESSAGE
        : isLocked && this.hasPinnedBuOnBuScopedTemplate
          ? WorkflowTemplateGenerateDialog.BU_FIELD_LOCKED_TO_TEMPLATE_TOOLTIP
          : isLocked && this.isBusinessUnitAdmin
            ? "Your account is scoped to this business unit. The selection cannot be changed."
            : "Select a Business Unit from the list.";
      const showBuFieldTitle =
        buMisconfigured ||
        (isLocked && (this.hasPinnedBuOnBuScopedTemplate || this.isBusinessUnitAdmin));
      const emptyBuLabel = buMisconfigured
        ? "Unavailable — contact support"
        : isLoading
          ? "Loading Business Units…"
          : "Select a Business Unit";
      return html`
        <span
          class="tooltip-trigger field-trigger"
          title=${showBuFieldTitle ? buTip : ""}
          @mouseenter=${(e: Event) => this.showCustomTooltip(e, buTip, "bottom")}
          @mouseleave=${() => this.hideCustomTooltip()}
          @focusin=${(e: Event) => this.showCustomTooltip(e, buTip, "bottom")}
          @focusout=${() => this.hideCustomTooltip()}>
          <jha-form-select
            class="variable-field-select"
            required
            .warning=${effectiveError}
            ?showWarning=${showBuWarning}
            .options=${buOptions}
            labelKey="name"
            valueKey="value"
            data-id="BuiltinBusUnitVariable"
            data-auto-id="GenerateDialogBusUnitSelect"
            .value=${this.variableValues[key] || ""}
            @change=${(e: Event) => this.onVariableValueChange(variable, e)}
            ?disabled=${isLoading || isLocked}
            isValid
            emptyOptionLabel=${emptyBuLabel}
          >
          </jha-form-select>
        </span>
        ${showBuWarning && effectiveError
          ? html` <div class="error-message">${effectiveError}</div> `
          : ""}
      `;
    }

    return html`
      <jha-form-input
        required
        data-auto-id="GenerateDialogVariableInput"
        .value=${this.variableValues[key] || ""}
        @input=${(e: Event) => this.onVariableValueChange(variable, e)}
        ?invalid=${hasError}>
      </jha-form-input>
      ${hasError ? html` <div class="error-message">${errorMessage}</div> ` : ""}
    `;
  }

  private renderVariablePairLabelCell(
    variable: WorkflowTemplateVariable,
    variableIndex: number,
    placement: "left" | "right",
  ) {
    return html`<div class="variable-label-cell variable-label-cell--pair-${placement}">
      ${this.buildVariableLabelRow(variable, variableIndex)}
    </div>`;
  }

  private renderVariablePairFieldCell(variable: WorkflowTemplateVariable, placement: "left" | "right") {
    return html`<div class="variable-field-cell variable-field-cell--pair-${placement}">
      ${this.renderVariableFieldContent(variable)}
    </div>`;
  }

  /**
   * Render variable inputs grid
   */
  private renderVariableInputs() {
    // Variables are displayed in array order (order is preserved from DynamoDB)
    if (this.variables.length === 0) {
      return html`<p class="no-variables">No variables defined for this template.</p>`;
    }

    const pairs: TemplateResult[] = [];
    for (let i = 0; i < this.variables.length; i += 2) {
      const leftVar = this.variables[i];
      const rightVar = this.variables[i + 1];

      pairs.push(html`
        <div class="variable-grid-pair">
          ${this.renderVariablePairLabelCell(leftVar, i, "left")}
          ${rightVar
          ? this.renderVariablePairLabelCell(rightVar, i + 1, "right")
          : html`<div
                class="variable-label-cell variable-label-cell--pair-right variable-label-cell--empty"
                aria-hidden="true"></div>`}
          ${this.renderVariablePairFieldCell(leftVar, "left")}
          ${rightVar
          ? this.renderVariablePairFieldCell(rightVar, "right")
          : html`<div
                class="variable-field-cell variable-field-cell--pair-right variable-field-cell--empty"
                aria-hidden="true"></div>`}
        </div>
      `);
    }

    return html` <div class="variables-grid">${pairs}</div> `;
  }

  /**
   * Handle Cancel button click
   */
  private async onCancelClick(e: Event) {
    e?.preventDefault();
    e?.stopPropagation();

    if (this.isGenerating) {
      if (!(await this.confirmAbandonDuringGenerate())) {
        return;
      }
      this.abandonGenerate();
      return;
    }

    if (this.isFormDirty() && !this.isReadOnlyUser) {
      if (!(await this.showUnsavedConfirmation())) {
        return;
      }
    }

    this.dispatchForceClose();
  }

  /**
   * Handle Generate Workflow button click
   * Makes the API call directly and shows result before closing
   */
  private async onGenerateClick() {
    // Prevent double-click
    if (this.isGenerating) {
      return;
    }

    if (this.isBuScopedTemplateMissingPinnedBu) {
      showToastMessage(WorkflowTemplateGenerateDialog.BU_SCOPED_MISSING_PINNED_MESSAGE, "danger");
      return;
    }

    // Validate all fields
    if (!this.isValid()) {
      showToastMessage("Please fix validation errors before proceeding.", "danger");
      return;
    }

    // Set loading state
    const seq = ++this._generateSeq;
    this.isGenerating = true;
    this.apiResult = null;
    this._generateAbandoned = false;

    // Timeout controller - prevent hanging forever
    // 90 seconds to allow for complex workflow generation (DB writes, Lambda triggers, etc.)
    const timeoutMs = 90000;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
      // Get values
      const variableValues = this.getValues();

      // Build request body
      // CASING FIX: Use camelCase property names to match API conventions

      // Determine workflowName - need case-insensitive lookup because variable names 
      // can be any casing (e.g., "workflowname", "WorkflowName", "workflowName")
      const findVariableValue = (keys: string[]): string | undefined => {
        for (const key of keys) {
          // Check exact match first
          if (variableValues[key]) return variableValues[key];
          // Then check case-insensitive match
          const lowerKey = key.toLowerCase();
          const matchingKey = Object.keys(variableValues).find(k => k.toLowerCase() === lowerKey);
          if (matchingKey && variableValues[matchingKey]) return variableValues[matchingKey];
        }
        return undefined;
      };

      const rawWorkflowName = findVariableValue(['workflowName', 'WorkflowName', 'workflow_name', 'workflowname']) ||
        `${this.templateName}-Workflow-${Date.now()}`;

      // Sanitize workflow name: remove slashes and other path-like characters
      // Workflow names should not contain path separators as they become part of S3 paths
      let workflowName = rawWorkflowName.replace(/[\/\\]/g, '_').trim();
      if (!workflowName) {
        workflowName = `${this.templateName}-Workflow-${Date.now()}`.replace(/[\/\\]/g, '_').trim();
      }

      // Suffix-only path for generated workflows (PUSH and PULL) — mirrors CreateWorkflowTabView / ACME-1588.
      const sourceLocationPath = `/${workflowName}/`;

      // The backend's template-create-workflow endpoint requires a top-level
      // `businessUnitParticipant` field (TemplateProxy.CreateWorkflowFromTemplateAsync
      // rejects the request with "Business unit is required" when it is empty).
      // The same value already lives in variableValues under the BUILTIN_BUSUNIT_VARIABLE
      // key (the BU slug from businessunit-all-values), so mirror it to the top level.
      // The backend's ConvertToBUIdName resolver accepts the slug and normalizes it to
      // the canonical BusinessUnitId on the persisted workflow.
      const buVariableKey = Object.keys(variableValues).find((k) => isBuiltinBusUnitVariableName(k));
      const businessUnitParticipant = buVariableKey ? (variableValues[buVariableKey] || "").trim() : "";

      const requestBody = {
        variableValues: variableValues,
        sourceLocationPath: sourceLocationPath,
        workflowName: workflowName,
        businessUnitParticipant: businessUnitParticipant,
      };

      // Call API with timeout protection
      const generateUrl = `${Apis.GENERATE_WORKFLOW_FROM_TEMPLATE}/${fixedEncodeURIComponent(this.templateName)}/template-create-workflow`;

      // Create a timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('TIMEOUT'));
        }, timeoutMs);
      });

      // Race between API call and timeout
      const response = await Promise.race([
        postRequest(generateUrl, requestBody),
        timeoutPromise
      ]);

      // Clear timeout since we got a response
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (seq !== this._generateSeq) return;

      // No early `return` in this block: the result dialog is shown after the try/finally,
      // and `return` inside try skips it (silent failure — user sees only a spinner blink).
      if (response === null || response === undefined) {
        this.apiResult = {
          success: false,
          message: "No response from server. Please try again."
        };
      } else if (response?.details?.length > 0) {
        // Show the service's own text. Rewriting it by substring misreported the failure:
        // "Workflow already exists." (workflow id collision) was shown as a duplicate-name
        // error, which sends the user hunting for a name conflict that does not exist.
        this.apiResult = {
          success: false,
          message:
            getErrorMessage(response.details).trim() ||
            "Failed to generate workflow. Please try again.",
        };
      } else {
        const createdWorkflowName = response?.workflowName || requestBody.workflowName;
        this.apiResult = {
          success: true,
          message: `Workflow "${createdWorkflowName}" created successfully!`,
          workflowName: createdWorkflowName
        };
      }

    } catch (error: any) {
      // Clear timeout if still pending
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (seq !== this._generateSeq) return;

      // Determine user-friendly error message
      let errorMessage = "Unable to connect to the server. Please check your connection and try again.";

      if (error?.message === 'TIMEOUT') {
        errorMessage = "Request timed out. The server is taking too long to respond. Please try again.";
      } else if (error?.message?.includes('NetworkError') || error?.message?.includes('Failed to fetch')) {
        errorMessage = "Network error. Please check your internet connection and try again.";
      } else if (error?.message) {
        // Include actual error message for debugging but keep it user-friendly
        errorMessage = `An error occurred: ${error.message}`;
      }

      this.apiResult = {
        success: false,
        message: errorMessage
      };
    } finally {
      // ALWAYS clear timeout - defensive cleanup in case it wasn't cleared above
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (seq === this._generateSeq) {
        this.isGenerating = false;
        this.requestUpdate();
      }
    }

    if (seq !== this._generateSeq) return;

    // The user already left and was told to check the Workflows page; this dialog is gone,
    // so showing its result now would land on an unrelated page.
    if (this._generateAbandoned) {
      this.apiResult = null;
      return;
    }

    if (this.apiResult) {
      await this.presentGenerateResult();
    }
  }

  /**
   * Show success/error result via openJhftDialog.
   */
  private async presentGenerateResult() {
    if (!this.apiResult) return;

    if (this.apiResult.success) {
      await openJhftDialog({
        variant: "success",
        title: "Generate Workflow",
        message: this.apiResult.message,
        successLabel: "Done",
      });
      this.onCloseAfterResult();
      return;
    }

    const result = await openJhftDialog({
      variant: "error",
      title: "Generate Workflow",
      message: this.apiResult.message,
      retryLabel: "Try Again",
      closeLabel: "Close",
    });
    if (result === "retry") {
      this.onTryAgain();
    } else {
      this.onCloseAfterResult();
    }
  }

  /**
   * Close dialog after viewing result
   */
  private onCloseAfterResult() {
    const dialog = this.shadowRoot?.querySelector("jha-dialog");
    if (dialog && (dialog as any).dispatchDialogEvent) {
      // Pass the result back - success returns workflow name, cancel returns null
      const result = this.apiResult?.success ? { workflowName: this.apiResult.workflowName } : null;
      (dialog as any).dispatchDialogEvent("confirm", result);
    }
  }

  /**
   * Reset form to try again after error
   */
  private onTryAgain() {
    this.apiResult = null;
    this.isGenerating = false;
  }

  /**
   * Render dialog buttons
   */
  private renderButtons() {
    // Disable Generate button if form is invalid or currently generating
    const isButtonDisabled = this.isGenerating || !this.canSubmit();

    return html`
      <div class="button-container">
        <jha-button
          outline
          large
          sync
          data-auto-id="GenerateDialogCancelBtn"
          @click=${this.onCancelClick}>
          ${this.isGenerating ? "Cancel Request" : "Cancel"}
        </jha-button>
        <jha-button
          large
          sync
          data-auto-id="GenerateDialogGenerateBtn"
          ?disabled=${isButtonDisabled}
          ?loading=${this.isGenerating}
          @click=${this.onGenerateClick}>
          ${this.isGenerating ? "Generating..." : "Generate Workflow"}
        </jha-button>
      </div>
    `;
  }

  /**
   * Main render method.
   * Renders own <jha-dialog> element like AddWorkflowStepPopup for proper hideCancel support.
   * Unsaved, abandon-while-generating, success, and error prompts use openJhftDialog on document.body.
   */
  render() {
    return html`
      <jha-dialog hide-confirm>
        <article slot="dialog-content">
          <h2 style="margin: 0 0 24px 0; font-size: 20px; font-weight: 500; text-align: center;">Generate Workflow</h2>
          <div class="generate-workflow-form">
            <div class="form-header">
              <!-- Header with optional dev helper button -->
              <div class="header-with-autofill">
                ${this.templateName ? html`<h3 style="margin: 0; font-size: 16px; font-weight: 500;">${this.templateName}</h3>` : ''}
                
                <!-- 🧪 DEV HELPER: Auto-fill button (only visible in local dev mode) -->
                ${this.showDevHelpers ? html`
                  <span
                    class="tooltip-trigger"
                    @mouseenter=${(e: Event) =>
          this.showCustomTooltip(
            e,
            "🧪 DEV ONLY: Auto-fill all fields with valid test data matching regex patterns",
            "left"
          )}
                    @mouseleave=${() => this.hideCustomTooltip()}>
                    <button class="test-autofill-btn" data-auto-id="GenerateDialogAutoFillBtn" @click=${this.onAutoFillClick}>
                      <jha-icon-wand-magic-sparkles></jha-icon-wand-magic-sparkles>
                      <span>Auto-Fill</span>
                      <span class="test-badge">Dev</span>
                    </button>
                  </span>
                ` : ''}
              </div>
              
              ${this.isBuScopedTemplateMissingPinnedBu
        ? html`<p class="bu-misconfig-banner" role="alert">
                    ${WorkflowTemplateGenerateDialog.BU_SCOPED_MISSING_PINNED_MESSAGE}
                  </p>`
        : ""}
              
              <p class="instructions">All fields are required. Hover over the icons for details.</p>
              <div class="icon-instructions">
                <span class="icon-hint">
                  <jha-icon-document></jha-icon-document>
                  <span>Click to copy description</span>
                </span>
                <span class="icon-hint">
                  <jha-icon-information></jha-icon-information>
                  <span>Click to copy regex description</span>
                </span>
                <span class="icon-hint">
                  <jha-icon-code></jha-icon-code>
                  <span>Click to copy regex</span>
                </span>
              </div>
            </div>

            <!-- Scrollable container for variables -->
            <div class="variables-scroll-container">${this.renderVariableInputs()}</div>

            <!-- Footer separator -->
            <div class="form-footer"></div>

            <!-- Buttons -->
            ${this.renderButtons()}
          </div>

          <!-- Portal tooltip — position:fixed escapes any overflow ancestor -->
          ${this.renderCustomTooltip()}
        </article>
      </jha-dialog>
    `;
  }

  /**
   * Renders the single portal-style tooltip. Placed at the dialog root so its
   * `position: fixed` cannot be clipped by the variables scroll container.
   * `transform` re-anchors the tooltip relative to its computed (x, y).
   */
  private renderCustomTooltip() {
    if (!this.customTooltip) return "";
    const { text, x, y, side } = this.customTooltip;
    let transform = "";
    switch (side) {
      case "right":
        transform = "translateY(-50%)";
        break;
      case "left":
        transform = "translate(-100%, -50%)";
        break;
      case "bottom":
        transform = "translateX(-50%)";
        break;
      case "top":
        transform = "translate(-50%, -100%)";
        break;
    }
    const styleStr = `left:${x}px;top:${y}px;transform:${transform};`;
    return html`
      <div class="custom-tooltip side-${side}" style=${styleStr} role="tooltip">${text}</div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "workflow-template-generate-dialog": WorkflowTemplateGenerateDialog;
  }
}
